import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Empty, SpinLoading } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError } from "../../components/Toast";
import useNavigation from "../../hooks/useNavigation";
import useGlobalTheme from "../../hooks/useGlobalTheme";
import { formatMessageTime, formatPrice } from "../../utils";

const walletOutImg = require("../../assets/images/wallet/walletOut.png");
const walletInImg = require("../../assets/images/wallet/walletIn.png");
const takeOrderAddImg = require("../../assets/images/wallet/takeOrderAdd.png");
const pendingOrderReturnImg = require("../../assets/images/wallet/pendingOrderReturn.png");
const pendingOrderDeductImg = require("../../assets/images/wallet/pendingOrderDeduct.png");
const transOfficialImg = require("../../assets/images/wallet/transOfficial.png");

enum BALANCE_TYPE {
  ALL = -1,
  WALLET_IN = 0,
  WALLET_OUT = 1,
  TAKE_ORDER_ADD = 4,
  PENDING_ORDER_DEDUCT = 5,
  TAKE_ORDER_CANCEL = 6,
  PENDING_ORDER_RETURN = 7,
  TRANS_OFFICIAL = 8,
}

type WalletLogItem = {
  id: number;
  amount: number;
  balance_type: number;
  created: string;
  note?: string;
  [key: string]: any;
};

const TYPE_META: Record<number, { label: string; icon: any }> = {
  [BALANCE_TYPE.WALLET_IN]: { label: "转入", icon: walletInImg },
  [BALANCE_TYPE.WALLET_OUT]: { label: "转出", icon: walletOutImg },
  [BALANCE_TYPE.TAKE_ORDER_ADD]: { label: "订单买入", icon: takeOrderAddImg },
  [BALANCE_TYPE.PENDING_ORDER_DEDUCT]: {
    label: "订单冻结",
    icon: pendingOrderDeductImg,
  },
  [BALANCE_TYPE.TAKE_ORDER_CANCEL]: {
    label: "挂单取消",
    icon: pendingOrderReturnImg,
  },
  [BALANCE_TYPE.PENDING_ORDER_RETURN]: {
    label: "挂单退回",
    icon: pendingOrderReturnImg,
  },
  [BALANCE_TYPE.TRANS_OFFICIAL]: { label: "充值", icon: transOfficialImg },
};

const typeTabs = [
  { key: BALANCE_TYPE.ALL, label: "全部" },
  { key: BALANCE_TYPE.WALLET_IN, label: "转入" },
  { key: BALANCE_TYPE.WALLET_OUT, label: "转出" },
  { key: BALANCE_TYPE.TAKE_ORDER_ADD, label: "买入" },
  { key: BALANCE_TYPE.PENDING_ORDER_DEDUCT, label: "冻结" },
  { key: BALANCE_TYPE.PENDING_ORDER_RETURN, label: "退回" },
  { key: BALANCE_TYPE.TRANS_OFFICIAL, label: "兑换" },
];

const pad = (n: number) => String(n).padStart(2, "0");
const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (base: Date, days: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
};
const toMonthDayLabel = (dateStr: string) => {
  const d = new Date(dateStr.replace(/-/g, "/"));
  return `${pad(d.getMonth() + 1)}月${pad(d.getDate())}日`;
};

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f7f8ff;
`;

const TypeTabs = styled.div`
  flex-shrink: 0;
  display: flex;
  overflow-x: auto;
  height: 40px;
  background: #fff;
`;

const TypeTab = styled.div<{ $active?: boolean }>`
  flex: 0 0 20%;
  min-width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.inputPlaceholderColor || "#999"};
`;

const TimeBox = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  font-size: 13px;
  color: #999;
`;

const TimeTabs = styled.div`
  display: flex;
  align-items: center;
`;

const TimeTab = styled.button<{ $active?: boolean }>`
  margin-left: 5px;
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : "#999"};
  background: #fff;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : "#999"};
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 10px 12px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  margin: 6px 0;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
`;

const MyBill = () => {
  const theme = useGlobalTheme();
  const { navigate } = useNavigation();
  const { walletStore, userStore } = useStores();
  const username = userStore.currentUser?.username || "";
  const [type, setType] = useState(BALANCE_TYPE.ALL);
  const [list, setList] = useState<WalletLogItem[]>([]);
  const [page, setPage] = useState(1);
  const [isEnd, setIsEnd] = useState(false);
  const [loading, setLoading] = useState(false);

  const todayStr = useMemo(() => toDateStr(new Date()), []);
  const [dateTime, setDateTime] = useState(todayStr);
  const [timeStr, setTimeStr] = useState(toMonthDayLabel(todayStr));

  const setQueryDate = (next: string) => {
    setDateTime(next);
    setTimeStr(toMonthDayLabel(next));
  };

  const goToday = () => setQueryDate(todayStr);

  const shiftDate = (days: number) => {
    const next = toDateStr(addDays(new Date(dateTime.replace(/-/g, "/")), days));
    // 不允许查到未来日期
    if (next > todayStr) return;
    setQueryDate(next);
  };

  const load = async (nextPage: number, reset: boolean) => {
    try {
      if (!reset && isEnd) return;
      setLoading(true);
      const filters: Record<string, any> = {
        current: nextPage,
        pageSize: 30,
        created_ge: `${dateTime} 00:00:00`,
        created_lt: `${dateTime} 23:59:59`,
      };
      if (type !== BALANCE_TYPE.ALL) {
        filters.balance_type = type;
      }
      const res = await walletStore.getWalletLog(filters);
      const nextData: WalletLogItem[] = Array.isArray(res?.data)
        ? res.data
        : res?.data?.items || [];
      const total = res?.total ?? nextData.length;
      setList((prev) => {
        const merged = reset ? nextData : [...prev, ...nextData];
        setIsEnd(total === merged.length || nextData.length === 0);
        return merged;
      });
      setPage(nextPage);
    } catch (e: any) {
      ToastError(e.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setList([]);
    setIsEnd(false);
    load(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, dateTime]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (loading || isEnd) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      load(page + 1, false);
    }
  };

  return (
    <PageWrap>
      <PageHeader title="我的账单" />
      <Body>
        <TypeTabs>
          {typeTabs.map((t) => (
            <TypeTab
              key={t.key}
              $active={type === t.key}
              onClick={() => setType(t.key)}
            >
              {t.label}
            </TypeTab>
          ))}
        </TypeTabs>
        <TimeBox>
          <span>
            查询时间：
            <span style={{ color: "#333" }}>{timeStr}</span>
          </span>
          <TimeTabs>
            <TimeTab type="button" onClick={() => shiftDate(-1)}>
              上一日
            </TimeTab>
            <TimeTab
              type="button"
              $active={dateTime === todayStr}
              onClick={goToday}
            >
              今日
            </TimeTab>
            <TimeTab
              type="button"
              disabled={dateTime >= todayStr}
              onClick={() => shiftDate(1)}
            >
              下一日
            </TimeTab>
          </TimeTabs>
        </TimeBox>
        <ScrollArea onScroll={onScroll}>
          {!loading && list.length === 0 ? (
            <Empty description="暂无数据" style={{ padding: 40 }} />
          ) : (
            list.map((item) => {
              const meta = TYPE_META[item.balance_type] || {
                label: item.note || "账单",
                icon: walletInImg,
              };
              return (
                <Item
                  key={item.id}
                  onClick={() =>
                    navigate("WalletLogDetail", {
                      id: item.id,
                      walletLog: item,
                    })
                  }
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img src={meta.icon} alt="" width={40} height={40} />
                    <div style={{ marginLeft: 10 }}>
                      <div style={{ fontWeight: 600 }}>
                        {meta.label}
                        {username ? `-${username}` : ""}
                      </div>
                      <div style={{ color: "#999", fontSize: 12, marginTop: 4 }}>
                        {formatMessageTime(new Date(item.created)) ||
                          item.created}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, textAlign: "right" }}>
                    {formatPrice(item.amount || 0)} 喜鹊币
                  </div>
                </Item>
              );
            })
          )}
          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 20 }}
            >
              <SpinLoading color={theme.colors.primary} />
            </div>
          ) : null}
        </ScrollArea>
      </Body>
    </PageWrap>
  );
};

export default observer(MyBill);
